import React, { Suspense, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { useAuth } from '../context/AuthContext';
import { gmailInviteService } from '../services/gmailInviteService';
import {
  BillingView, OPEN_BILLING_EVENT, OPEN_REFER_EARN_EVENT, UPGRADE_DISMISSED_EVENT,
} from '../utils/freeCreditsPrompts';

import { InviteOfferModal } from './InviteOffer';
import { ReferralModal } from './ReferralModal';

// Loaded on demand: the billing modal pulls in the payment services.
const BillingModal = React.lazy(() => import('./BillingModal').then((module) => ({ default: module.BillingModal })));

type Open = 'refer' | 'invite' | BillingView | null;

/** The invite prompt after a closed upgrade window shows once per browser session. */
const PROMPTED_KEY = 'free-credits:invite-after-upgrade';
const alreadyPrompted = (): boolean => {
  try { return globalThis.sessionStorage.getItem(PROMPTED_KEY) === '1'; } catch { return false; }
};
const markPrompted = (): void => {
  try { globalThis.sessionStorage.setItem(PROMPTED_KEY, '1'); } catch { /* storage blocked: may show again */ }
};

const AFTER_UPGRADE_COPY = {
  title: 'Not ready to upgrade? Get free credits',
  description: 'Invite your Gmail contacts to 8080.AI with one press, and earn up to 25 credits for each friend '
    + 'who joins.',
};

/** Invite all needs a Google mailbox; everyone else gets Refer & earn's link and reel rows instead. */
const promptAfterUpgrade = async (setOpen: (open: Open) => void): Promise<void> => {
  if (alreadyPrompted()) return;
  markPrompted();
  const status = await gmailInviteService.status().catch(() => null);

  setOpen(status?.available && status.google_account ? 'invite' : 'refer');
};

/** The windows the More menu and the upgrade window ask for, while someone is signed in. */
const useFreeCreditEvents = (signedIn: boolean, isFree: boolean, setOpen: (open: Open) => void) => {
  useEffect(() => {
    if (!signedIn) return undefined;
    const listeners: [string, (event: Event) => void][] = [
      [OPEN_REFER_EARN_EVENT, () => setOpen('refer')],
      [OPEN_BILLING_EVENT, (event) => setOpen((event as globalThis.CustomEvent<BillingView>).detail)],
      [UPGRADE_DISMISSED_EVENT, () => { if (isFree) void promptAfterUpgrade(setOpen); }],
    ];

    listeners.forEach(([name, listener]) => window.addEventListener(name, listener));

    return () => listeners.forEach(([name, listener]) => window.removeEventListener(name, listener));
  }, [signedIn, isFree, setOpen]);
};

const OpenWindow: React.FC<{ open: Open; close: () => void }> = ({ open, close }) => {
  if (open === 'plans' || open === 'credits') {
    return <Suspense fallback={null}><BillingModal initialView={open} onClose={close} /></Suspense>;
  }
  // Google brings Invite all back to Refer & earn: the offer it would reopen promises the 10-credit bonus.
  if (open === 'invite') {
    return createPortal(
      <InviteOfferModal copy={AFTER_UPGRADE_COPY} onClose={close} returnTo="refer" source="after_upgrade" />,
      document.body,
    );
  }

  // Refer & earn opens here only from the planning More menu's "Free credits" item.
  return <ReferralModal isOpen={open === 'refer'} onClose={close} source="planning_more_menu" />;
};

/**
 * Refer & earn and the billing window (Upgrade / Add credits) from the More menu, and the Invite all prompt after
 * a free user closes the upgrade window.
 */
export const FreeCreditsHost: React.FC = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState<Open>(null);

  useFreeCreditEvents(Boolean(user), (user?.subscription_tier || 'free').toLowerCase() === 'free', setOpen);

  return <OpenWindow open={open} close={() => setOpen(null)} />;
};
